import React, { useMemo, useState } from "react";
import { CrudTestAutomation, TestResult } from "./entityCrudTestHelper";
import { COMPLETE_QA_REGISTRY, COMPLETE_TEST_PAYLOADS } from "./completeQARegistry";
import {
  FlaskConical, PlayCircle, CheckCircle2, XCircle, Search, ChevronDown,
  Loader2, Eraser, Clock, ListChecks, Eye,
} from "lucide-react";

import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Separator } from "../../components/ui/separator";
import { cn } from "../../lib/utils";

// ═══════════════════════════════════════════════════════════════════════
// QA AUTOMATION DASHBOARD
// Visual language matches UserManagement: white surface, rounded-[2rem]
// cards, slate/blue/emerald accents, black tracking-tight headings.
// ═══════════════════════════════════════════════════════════════════════

type RunState = "idle" | "queued" | "running" | "pass" | "fail";

interface DashboardState {
  selectedPages: string[];
  isRunning: boolean;
  allResults: Record<string, TestResult[]>;
  runState: Record<string, RunState>;
  expandedResultPage: string | null;
  search: string;
}

// ─── Group pages by a coarse category derived from the registry key ─────
const CATEGORY_MAP: Array<{ test: RegExp; label: string }> = [
  { test: /icd/i, label: "ICD configuration" },
  { test: /activit/i, label: "Activity configuration" },
  { test: /drug/i, label: "Drug configuration" },
  { test: /benefit/i, label: "Benefit configuration" },
  { test: /network|provider|polic|patient|insurance|doctor|specialty|vip/i, label: "Core configuration" },
];

function categoryFor(pageKey: string): string {
  const match = CATEGORY_MAP.find((c) => c.test.test(pageKey));
  return match ? match.label : "Other";
}

const statusConfig: Record<RunState, { dot: string; label: string }> = {
  idle: { dot: "bg-slate-300", label: "Not run" },
  queued: { dot: "bg-amber-400", label: "Queued" },
  running: { dot: "bg-blue-500 animate-pulse", label: "Running" },
  pass: { dot: "bg-emerald-500", label: "Passed" },
  fail: { dot: "bg-red-500", label: "Failed" },
};

const QADashboard: React.FC = () => {
  const [state, setState] = useState<DashboardState>({
    selectedPages: [],
    isRunning: false,
    allResults: {},
    runState: {},
    expandedResultPage: null,
    search: "",
  });

  const pageKeys = useMemo(() => Object.keys(COMPLETE_QA_REGISTRY), []);

  const groups = useMemo(() => {
    const byCategory: Record<string, string[]> = {};
    pageKeys.forEach((key) => {
      const cat = categoryFor(key);
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push(key);
    });
    return byCategory;
  }, [pageKeys]);

  const filteredPageKeys = useMemo(() => {
    if (!state.search.trim()) return pageKeys;
    const q = state.search.trim().toLowerCase();
    return pageKeys.filter(
      (key) =>
        COMPLETE_QA_REGISTRY[key].pageName.toLowerCase().includes(q) ||
        key.toLowerCase().includes(q)
    );
  }, [pageKeys, state.search]);

  // ─── Selection ──────────────────────────────────────────────────────
  const togglePageSelection = (pageKey: string) => {
    setState((prev) => ({
      ...prev,
      selectedPages: prev.selectedPages.includes(pageKey)
        ? prev.selectedPages.filter((p) => p !== pageKey)
        : [...prev.selectedPages, pageKey],
    }));
  };

  const toggleGroupSelection = (groupKeys: string[]) => {
    setState((prev) => {
      const allSelected = groupKeys.every((k) => prev.selectedPages.includes(k));
      return {
        ...prev,
        selectedPages: allSelected
          ? prev.selectedPages.filter((k) => !groupKeys.includes(k))
          : Array.from(new Set([...prev.selectedPages, ...groupKeys])),
      };
    });
  };

  const selectAll = () => setState((prev) => ({ ...prev, selectedPages: pageKeys }));
  const deselectAll = () => setState((prev) => ({ ...prev, selectedPages: [] }));

  // ─── Run QA for selected pages ──────────────────────────────────────
  const runQAForSelectedPages = async () => {
    setState((prev) => ({
      ...prev,
      isRunning: true,
      runState: Object.fromEntries(prev.selectedPages.map((k) => [k, "queued" as RunState])),
    }));

    const results: Record<string, TestResult[]> = {};

    for (const pageKey of state.selectedPages) {
      setState((prev) => ({ ...prev, runState: { ...prev.runState, [pageKey]: "running" } }));

      try {
        const config = COMPLETE_QA_REGISTRY[pageKey];
        const testPayload = COMPLETE_TEST_PAYLOADS[pageKey];

        const qa = new CrudTestAutomation(config);
        let testId: string | number | null = null;

        const addResult = await qa.testAdd(testPayload);
        if (addResult.status === "pass" && addResult.details?.addedId) {
          testId = addResult.details.addedId;
        }

        await qa.testFetch();
        if (testId) {
          await qa.testUpdate(testId, { ...testPayload, relation_name: `${testPayload.relation_name}_UPD` });
          await qa.testSoftDelete(testId);
          if (addResult.status === "pass") {
            await qa.testRestore(testId);
            await qa.testHardDelete(testId);
          }
        }

        const pageResults = qa.getResults();
        results[pageKey] = pageResults;

        const hasFailure = pageResults.some((r) => r.status === "fail");
        setState((prev) => ({
          ...prev,
          runState: { ...prev.runState, [pageKey]: hasFailure ? "fail" : "pass" },
        }));
      } catch (error) {
        results[pageKey] = [
          {
            testName: "Unexpected error",
            status: "fail",
            duration: 0,
            error: error instanceof Error ? error.message : String(error),
          } as TestResult,
        ];
        setState((prev) => ({ ...prev, runState: { ...prev.runState, [pageKey]: "fail" } }));
      }
    }

    setState((prev) => ({ ...prev, isRunning: false, allResults: { ...prev.allResults, ...results } }));
  };

  const clearResults = () => setState((prev) => ({ ...prev, allResults: {}, runState: {} }));

  // ─── Summary ──────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    let totalPass = 0;
    let totalFail = 0;
    let totalDuration = 0;

    Object.values(state.allResults).forEach((results) => {
      totalPass += results.filter((r) => r.status === "pass").length;
      totalFail += results.filter((r) => r.status === "fail").length;
      totalDuration += results.reduce((sum, r) => sum + (r.duration || 0), 0);
    });

    return { totalPass, totalFail, totalDuration };
  }, [state.allResults]);

  const totalTests = summary.totalPass + summary.totalFail;
  const hasResults = Object.keys(state.allResults).length > 0;

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F8FAFC] p-8 pb-24 font-sans">
      <div className="max-w-7xl mx-auto space-y-10 animate-in fade-in duration-700">

        {/* Header — matches UserManagement's header pattern, white surface */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 pb-4">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="bg-slate-900 p-2.5 rounded-2xl shadow-2xl shadow-slate-200">
                <FlaskConical className="w-5 h-5 text-white" />
              </div>
              <Badge variant="outline" className="bg-white border-slate-200 text-slate-400 text-[9px] font-black tracking-[0.2em] px-3 h-6 rounded-lg">
                QA Suite
              </Badge>
            </div>
            <div className="space-y-1">
              <h1 className="text-5xl font-black text-slate-900 tracking-tighter">QA Dashboard</h1>
              <p className="text-slate-500 font-medium text-lg max-w-2xl leading-relaxed">
                Run automated CRUD checks across every registered entity and review pass/fail results.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="bg-white border border-slate-200 p-1.5 rounded-[1.5rem] shadow-sm flex items-center gap-4">
              <div className="flex items-center gap-3 pl-4 pr-2 py-2 border-r border-slate-100">
                <div className="p-2 rounded-xl bg-blue-50 text-blue-600">
                  <ListChecks className="w-4 h-4" />
                </div>
                <div className="flex flex-col leading-tight pr-4">
                  <span className="text-[9px] font-black text-slate-400 tracking-widest">Selected</span>
                  <span className="text-sm font-black text-slate-800">{state.selectedPages.length} of {pageKeys.length}</span>
                </div>
              </div>
              <Button
                onClick={runQAForSelectedPages}
                disabled={state.isRunning || state.selectedPages.length === 0}
                className="h-12 px-8 rounded-2xl bg-slate-900 hover:bg-slate-800 shadow-xl shadow-slate-200 gap-2 font-black text-xs tracking-widest disabled:opacity-40"
              >
                {state.isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                {state.isRunning ? "Running" : "Run tests"}
              </Button>
            </div>
          </div>
        </div>

        {/* Metrics — same card pattern as UserManagement */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { label: "Registered entities", val: pageKeys.length, icon: ListChecks, color: "text-blue-600", bg: "bg-blue-50" },
            { label: "Checks passed", val: summary.totalPass, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
            { label: "Checks failed", val: summary.totalFail, icon: XCircle, color: "text-red-600", bg: "bg-red-50" },
            { label: "Total runtime", val: `${(summary.totalDuration / 1000).toFixed(2)}s`, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
          ].map((s, i) => (
            <Card key={i} className="border-none shadow-sm shadow-slate-200/50 rounded-[2rem] bg-white group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <CardContent className="p-7 flex flex-col gap-4">
                <div className={cn("w-12 h-12 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110", s.bg)}>
                  <s.icon className={cn("w-6 h-6", s.color)} />
                </div>
                <div>
                  <p className="text-3xl font-black text-slate-900 tracking-tighter leading-none mb-1.5">{s.val}</p>
                  <p className="text-[10px] font-black text-slate-400 tracking-widest">{s.label.toUpperCase()}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Body: selector + results */}
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6 items-start">

          {/* ── LEFT: entity selector ── */}
          <Card className="border-none shadow-2xl shadow-slate-200/60 rounded-[2.5rem] overflow-hidden bg-white">
            <div className="p-6 space-y-4 border-b border-slate-100">
              <div className="flex items-center gap-2 h-11 px-4 rounded-2xl border border-slate-200 bg-slate-50 focus-within:ring-2 focus-within:ring-slate-900/10 transition-all">
                <Search className="w-4 h-4 text-slate-400 shrink-0" />
                <input
                  value={state.search}
                  onChange={(e) => setState((prev) => ({ ...prev, search: e.target.value }))}
                  placeholder="Filter entities…"
                  className="flex-1 bg-transparent text-xs font-bold text-slate-700 placeholder:text-slate-400 outline-none border-none"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={selectAll} disabled={state.isRunning}
                  className="flex-1 h-9 rounded-xl border-slate-200 text-[11px] font-black text-blue-600 hover:bg-blue-50 tracking-wide">
                  Select all
                </Button>
                <Button variant="outline" size="sm" onClick={deselectAll} disabled={state.isRunning}
                  className="flex-1 h-9 rounded-xl border-slate-200 text-[11px] font-black text-slate-500 hover:bg-slate-100 tracking-wide">
                  Clear
                </Button>
              </div>
            </div>

            <div className="max-h-[560px] overflow-y-auto">
              {Object.entries(groups).map(([groupName, groupKeys]) => {
                const visibleKeys = groupKeys.filter((k) => filteredPageKeys.includes(k));
                if (visibleKeys.length === 0) return null;
                const selectedInGroup = visibleKeys.filter((k) => state.selectedPages.includes(k)).length;

                return (
                  <div key={groupName} className="border-b border-slate-50 last:border-b-0">
                    <div className="px-6 py-3 flex items-center justify-between bg-slate-50/60">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-black text-slate-500 tracking-widest">{groupName.toUpperCase()}</span>
                        <Badge className="bg-slate-100 text-slate-500 border-none rounded-md text-[10px] font-bold px-1.5 py-0 shadow-none pointer-events-none">
                          {selectedInGroup}/{visibleKeys.length}
                        </Badge>
                      </div>
                      <button
                        onClick={() => toggleGroupSelection(visibleKeys)}
                        className="text-[11px] font-black text-blue-600 hover:text-blue-700"
                      >
                        Toggle
                      </button>
                    </div>

                    {visibleKeys.map((pageKey) => {
                      const isSelected = state.selectedPages.includes(pageKey);
                      const rs = state.runState[pageKey] ?? "idle";
                      return (
                        <label
                          key={pageKey}
                          className={cn(
                            "flex items-center gap-3 px-6 py-3 cursor-pointer transition-colors border-l-2",
                            isSelected ? "bg-blue-50/60 border-blue-500" : "border-transparent hover:bg-slate-50"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => togglePageSelection(pageKey)}
                            disabled={state.isRunning}
                            className="w-4 h-4 rounded accent-slate-900 cursor-pointer shrink-0"
                          />
                          <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", statusConfig[rs].dot)} title={statusConfig[rs].label} />
                          <span className="text-sm font-bold text-slate-700 flex-1 truncate">
                            {COMPLETE_QA_REGISTRY[pageKey].pageName}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* ── RIGHT: results ── */}
          <div className="space-y-4">
            {!hasResults && (
              <Card className="border-2 border-dashed border-slate-200 shadow-none rounded-[2.5rem] bg-white">
                <CardContent className="p-16 flex flex-col items-center text-center gap-3">
                  <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center">
                    <FlaskConical className="w-7 h-7 text-slate-300" />
                  </div>
                  <p className="text-lg font-black text-slate-900 tracking-tighter">No results yet</p>
                  <p className="text-sm text-slate-400 font-medium max-w-xs leading-relaxed">
                    Select one or more entities on the left, then run the suite to see pass/fail results here.
                  </p>
                </CardContent>
              </Card>
            )}

            {hasResults && (
              <Card className="border-none shadow-2xl shadow-slate-200/60 rounded-[2.5rem] overflow-hidden bg-white">
                <div className="flex items-center justify-between px-7 py-5 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-black text-slate-800 tracking-tight">Results by entity</span>
                    <Badge className="bg-emerald-50 text-emerald-700 border-none rounded-lg text-[10px] font-black px-2 py-1 shadow-none pointer-events-none">
                      {summary.totalPass} pass
                    </Badge>
                    <Badge className="bg-red-50 text-red-600 border-none rounded-lg text-[10px] font-black px-2 py-1 shadow-none pointer-events-none">
                      {summary.totalFail} fail
                    </Badge>
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearResults}
                    className="h-9 rounded-xl text-[11px] font-black text-slate-400 hover:text-red-600 hover:bg-red-50 gap-2 tracking-wide">
                    <Eraser className="w-3.5 h-3.5" />
                    Clear results
                  </Button>
                </div>

                <div>
                  {Object.entries(state.allResults).map(([pageKey, results]) => {
                    const pass = results.filter((r) => r.status === "pass").length;
                    const fail = results.filter((r) => r.status === "fail").length;
                    const duration = results.reduce((s, r) => s + (r.duration || 0), 0);
                    const isOpen = state.expandedResultPage === pageKey;

                    return (
                      <div key={pageKey} className="border-b border-slate-50 last:border-b-0">
                        <button
                          onClick={() =>
                            setState((prev) => ({
                              ...prev,
                              expandedResultPage: prev.expandedResultPage === pageKey ? null : pageKey,
                            }))
                          }
                          className="w-full flex items-center justify-between px-7 py-4 hover:bg-slate-50/60 transition-colors text-left"
                        >
                          <div className="flex items-center gap-3">
                            <ChevronDown className={cn("w-4 h-4 text-slate-400 transition-transform", isOpen && "rotate-180")} />
                            {fail > 0 ? (
                              <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                            )}
                            <span className="text-sm font-bold text-slate-800">{COMPLETE_QA_REGISTRY[pageKey].pageName}</span>
                          </div>
                          <div className="flex items-center gap-5 text-xs font-bold">
                            <span className="text-emerald-600">{pass} pass</span>
                            <span className="text-red-500">{fail} fail</span>
                            <span className="text-slate-400 w-16 text-right">{duration.toFixed(0)}ms</span>
                          </div>
                        </button>

                        {isOpen && (
                          <div className="px-7 pb-4 pl-14 space-y-2">
                            {results.map((result, idx) => (
                              <div
                                key={idx}
                                className={cn(
                                  "px-4 py-3 rounded-2xl border",
                                  result.status === "pass" ? "bg-emerald-50/60 border-emerald-100" : "bg-red-50/60 border-red-100"
                                )}
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <span className={cn("text-xs font-bold", result.status === "pass" ? "text-emerald-700" : "text-red-700")}>
                                    {result.testName}
                                  </span>
                                  <span className="text-[11px] font-bold text-slate-400">{result.duration.toFixed(2)}ms</span>
                                </div>
                                {result.error && (
                                  <p className="text-[11px] font-medium text-red-500 mt-1.5 break-all">{result.error}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QADashboard;